'use client'

import { useParams } from 'next/navigation'
import { useNavigation } from '@/components/NavigationProvider'
import { ButtonLink, Heading, Badge } from '@/components'
import { REQUESTED_KEYS as BADGE_KEYS } from '@/components/ui/molecules/Badge'
import hash from 'stable-hash'
import { getUrlFromNavItem } from '@/utilities/getUrlFromNavItem'
import { isRelevantNavItem } from '@/constants/navigationFilters'
import { defaultLocale } from '@/constants'
import type { TypedLocale } from 'payload'
import type { Navigation } from '@/payload-types'
import { Suspense, useMemo } from 'react'
import { getProductVariantFromLabel } from '@/locales/customLabels'
import { createTranslationResource } from '@/lib/translations'

interface NotFoundTemplateProps {
  homeLink: string
}

type LinkDescriptor = {
  href: string
  label: string
  variant: 'dog' | 'cat' | null
}

const NOT_FOUND_KEYS = [
  '404.title',
  '404.message',
  '404.go-home',
  '404.suggestions',
] as const

const NOT_FOUND_REQUEST_KEYS = [...NOT_FOUND_KEYS, ...BADGE_KEYS] as const

type NotFoundTranslationKey = (typeof NOT_FOUND_KEYS)[number]

const NOT_FOUND_BADGE_SKELETON_KEYS = [
  'badge-skeleton-primary',
  'badge-skeleton-secondary',
  'badge-skeleton-tertiary',
]

const resolveTranslations = (
  map: Record<string, string>,
): Record<NotFoundTranslationKey, string> =>
  NOT_FOUND_KEYS.reduce<Record<NotFoundTranslationKey, string>>(
    (acc, key) => {
      acc[key] = map[key] ?? key
      return acc
    },
    {} as Record<NotFoundTranslationKey, string>,
  )

function buildLinks(
  navigation: Navigation | null,
  locale: TypedLocale,
): LinkDescriptor[] {
  if (!navigation?.links) return []
  return navigation.links
    .filter((item) => isRelevantNavItem(item, locale))
    .map((item) => ({
      href: getUrlFromNavItem(item, locale),
      label: item.label,
      variant: getProductVariantFromLabel(item.label, locale),
    }))
}

export function NotFoundTemplate({ homeLink }: NotFoundTemplateProps) {
  const params = useParams()
  const { navigation } = useNavigation()
  const locale = (params?.locale as TypedLocale) || defaultLocale

  const translationResource = useMemo(
    () => createTranslationResource([...NOT_FOUND_REQUEST_KEYS], locale),
    [locale],
  )

  const links = useMemo(
    () => buildLinks(navigation as Navigation | null, locale),
    [navigation, locale],
  )
  const badgeLinks = useMemo(() => links.filter((l) => l.variant), [links])

  return (
    <div className="container py-28">
      <Suspense fallback={<NotFoundSkeleton hasBadges={badgeLinks.length > 0} />}>
        <NotFoundContent
          translationResource={translationResource}
          homeLink={homeLink}
          badgeLinks={badgeLinks}
          locale={locale}
        />
      </Suspense>
    </div>
  )
}

const NotFoundContent = ({
  translationResource,
  homeLink,
  badgeLinks,
  locale,
}: {
  translationResource: ReturnType<typeof createTranslationResource>
  homeLink: string
  badgeLinks: LinkDescriptor[]
  locale: TypedLocale
}) => {
  const translationMap = translationResource.read()
  const translations = resolveTranslations(translationMap)

  return (
    <>
      <div className="text-center mb-8">
        <Heading as="h1" text={translations['404.title']} size="4xl" />
        <p className="mb-4">{translations['404.message']}</p>
      </div>

      <div className="flex flex-col items-center gap-4">
        <ButtonLink
          text={translations['404.go-home']}
          href={homeLink}
          color="dark-grey"
        />

        {badgeLinks.length > 0 && (
          <>
            <p className="text-sm text-gray-600 mt-4 mb-2">
              {translations['404.suggestions']}
            </p>
            <div className="flex w-full flex-col lg:flex-row justify-center gap-4">
              {badgeLinks.map((link) => (
                <Badge
                  key={hash({ href: link.href, variant: link.variant, locale })}
                  variant={link.variant as 'dog' | 'cat'}
                  locale={locale}
                  translationMap={translationMap}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </>
  )
}

const NotFoundSkeleton = ({ hasBadges }: { hasBadges: boolean }) => (
  <div className="text-center animate-in fade-in">
    <div className="flex flex-col items-center gap-4 mb-8">
      <div
        className="gpf-skeleton gpf-skeleton--inline"
        style={{ width: '18rem', minHeight: '3.5rem' }}
        aria-hidden="true"
      >
        &nbsp;
      </div>
      <div
        className="gpf-skeleton gpf-skeleton--inline"
        style={{ width: '22rem', minHeight: '1.5rem' }}
        aria-hidden="true"
      >
        &nbsp;
      </div>
    </div>

    <div className="flex flex-col items-center gap-4">
      <div
        className="gpf-skeleton gpf-skeleton--inline"
        style={{ width: '10rem', minHeight: '2.75rem' }}
        aria-hidden="true"
      >
        &nbsp;
      </div>

      {hasBadges && (
        <div className="flex flex-col items-center gap-4 mt-4">
          <div
            className="gpf-skeleton gpf-skeleton--inline"
            style={{ width: '12rem', minHeight: '1rem' }}
            aria-hidden="true"
          >
            &nbsp;
          </div>
          <div className="flex flex-wrap justify-center gap-4 w-full">
            {NOT_FOUND_BADGE_SKELETON_KEYS.map((key) => (
              <div
                key={key}
                className="gpf-skeleton gpf-skeleton--inline"
                style={{ width: '6rem', minHeight: '2rem' }}
                aria-hidden="true"
              >
                &nbsp;
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  </div>
)
